<template>
  <div
    class="grid w-full gap-4 p-4 relative"
    v-loading="loading"
    element-loading-background="rgba(24, 33, 48, 0.8)"
  >
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2
        id="radix-_r_24_"
        class="text-lg font-semibold leading-none tracking-tight"
      >
        {{ id ? "Edit Alert Rule" : "Create Alert Rule" }}
      </h2>
      <p id="radix-_r_25_" class="text-sm text-muted-foreground">
        Configure an alert notification rule
      </p>
    </div>
    <div class="space-y-6 py-4">
      <div class="space-y-4">
        <h3 class="text-sm font-semibold">Basic Information</h3>
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            for="name"
          >
            Rule Name
          </label>
          <el-input
            id="name"
            placeholder="e.g., Critical Temperature Alert"
            v-model="form.rule_name"
          />
        </div>
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            for="priority"
          >
            Priority
          </label>
          <el-slider
            id="priority"
            v-model="form.priority"
            :min="0"
            :max="100"
            :step="1"
            show-input
          />
        </div>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Event Source (Select multiple)
          </label>
          <DeviceParams v-model="form.params_match" :device-type="form.type" />
        </div>
      </div>
      <div class="space-y-4">
        <h3 class="text-sm font-semibold">Cooldown Period</h3>
        <p class="text-sm text-muted-foreground">
          Prevents repeated notification messages for the same alert within the
          specified time window
        </p>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            for="cooldown"
          >
            Cooldown Duration
          </label>
          <SmartSelect
            id="cooldown"
            v-model="form.cooldown_min"
            :options="[
              { name: 'No cooldown', id: 0 },
              { name: '5 minutes', id: 5 },
              { name: '10 minutes', id: 10 },
              { name: '15 minutes (Recommended)', id: 15 },
              { name: '30 minutes', id: 30 },
              { name: '1 hour', id: 60 },
              { name: '2 hours', id: 120 },
              { name: '4 hours', id: 240 },
            ]"
            placeholder="Select cooldown duration"
            clearable
          />
          <p class="text-xs text-muted-foreground">
            If the same alert triggers multiple times, notification messages will
            only be sent once per cooldown period
          </p>
        </div>
        <div class="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-4">
          <div class="space-y-1">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              for="cooldownScope"
            >
              Cooldown Scope
            </label>
            <p class="text-xs text-muted-foreground">
              Decide whether all matched devices share one cooldown window, or
              each device keeps its own cooldown window.
            </p>
          </div>
          <SmartSelect
            id="cooldownScope"
            :value="cooldownScope"
            @input="handleCooldownScopeChange"
            :options="[
              { name: 'Whole rule', id: 'rule' },
              { name: 'Per device', id: 'device' },
            ]"
            placeholder="Select cooldown scope"
            style="width:100%;"
          />
          <div
            class="rounded-md border px-3 py-2 text-xs"
            :class="
              cooldownScope === 'device'
                ? 'border-blue-200 bg-blue-50 text-blue-700'
                : 'border-slate-200 bg-slate-50 text-slate-700'
            "
          >
            <template v-if="cooldownScope === 'device'">
              If this rule matches multiple devices, each device will be cooled
              down independently using
              <span class="font-mono">deviceKey</span>.
            </template>
            <template v-else>
              All devices matched by this rule will share the same cooldown
              window.
            </template>
          </div>
        </div>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            for="retryMaxAttempts"
          >
            Max Retry Attempts
          </label>
          <SmartSelect
            id="retryMaxAttempts"
            v-model="form.retry_max_attempts"
            :options="[
              { name: 'No retry', id: 0 },
              { name: '1 time', id: 1 },
              { name: '2 times', id: 2 },
              { name: '3 times', id: 3 },
              { name: '5 times', id: 5 },
            ]"
            placeholder="Select max retry attempts"
            clearable
          />
          <p class="text-xs text-muted-foreground">
            If sending fails, this channel will retry up to the selected number of
            times.
          </p>
        </div>
      </div>
      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold">Primary Recipients</h3>
            <p class="text-sm text-muted-foreground">
              Users who will receive the initial alert
            </p>
          </div>
        </div>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Select Recipients
          </label>
          <RecipientChannelCheckboxGroup
            key="outer"
            v-model="form.recipient_channels"
            :load-method="listNotificationRecipientInChannels"
            value-field="recipient_channel_id"
            label-field="recipient_name"
          />
        </div>
      </div>
      <EscalationSteps v-model="form.escalation_steps" />
      <div class="space-y-4">
        <h3 class="text-lg font-semibold">Message Template</h3>
        <div class="grid gap-4 md:grid-cols-1">
          <div class="space-y-3">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              for="messageTemplate"
            >
              Voice Asset
            </label>
            <UploadAudioWidget v-model="form.voice_asset_id" :label="'Select a voice asset for voice call message'" />
          </div>
        </div>
      </div>
      <div class="space-y-4">
        <h3 class="text-lg font-semibold">Message Template</h3>
        <div class="grid gap-4 md:grid-cols-2">
          <div class="space-y-3">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              for="messageTemplate"
            >
              Template Editor
            </label>
            <el-input
              type="textarea"
              rows="10"
              v-model="form.template"
              id="messageTemplate"
            >
            </el-input>
            <div class="space-y-2">
              <p class="text-xs text-muted-foreground font-medium">
                Available variables:
              </p>
              <div class="flex flex-wrap gap-1.5">
                <template>
                  <div
                    v-for="(v, i) in availableVariables"
                    :key="String(i)"
                    class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    {{ v }}
                  </div>
                </template>
              </div>
              <div class="text-xs text-muted-foreground mt-2">
                Supported timezone:
                <span class="font-mono">${timestamp:Asia/Hong_Kong}</span>,
                default is Hong Kong timezone
              </div>
              <div class="text-xs text-muted-foreground mt-2">
                Example: Alert <span class="font-mono">${eventContent}</span> on
                <span class="font-mono">${assetName}</span> (<span
                  class="font-mono"
                  >${deviceKey}</span
                >) at <span class="font-mono">${timestamp:Asia/Hong_Kong}</span>
              </div>
            </div>
          </div>
          <div class="space-y-3">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Message Preview
            </label>
            <div class="p-4 rounded-lg bg-[#181f2c] border border-[#232a3a]">
              <div
                class="bg-[#222e35] text-[#f8fafc] p-3 rounded-lg rounded-bl-none max-w-[85%] shadow-md"
              >
                <p class="text-sm whitespace-pre-wrap break-words">
                  {{ messagePreview }}
                </p>
                <div class="flex items-center justify-end gap-1 mt-1">
                  <span class="text-[10px] text-[#94a3b8]">10:30 AM</span>
                </div>
              </div>
            </div>
            <p class="text-xs text-muted-foreground">
              This is how your alert message will appear
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between">
      <div class="flex flex-row">
        <el-switch
          id="enabledSwitch"
          v-model="form.enabled"
          active-text="Enabled"
          inactive-text="Disabled"
        />
      </div>
      <div class="flex flex-row gap-2">
        <el-button @click="$emit('cancel')"> Cancel </el-button>
        <el-button :loading="submitting" type="primary" @click="handleSubmit">
          {{ id ? "Save Changes" : "Create Alert Rule" }}
        </el-button>
      </div>
    </div>
    <button
      @click="$emit('cancel')"
      type="button"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
    >
      <i class="icon-x size-4" />
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>
<script>
import {
  listNotificationRecipientInChannels,
  getNotificationRuleV2,
  NotificationRuleModel,
  updateNotificationRuleV2,
  createNotificationRuleV2,
} from "@/api";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import RecipientChannelCheckboxGroup from "./RecipientChannelCheckboxGroup.vue";
import EscalationSteps from "./EscalationSteps.vue";
import DeviceParams from "./DeviceParams.vue";
import UploadAudioWidget from "./UploadAudioWidget.vue";
import { extractErrorMessage } from "@/utils/errorUtils";

export default {
  name: "EditWhatsAppAlert",
  components: {
    SmartSelect,
    RecipientChannelCheckboxGroup,
    EscalationSteps,
    DeviceParams,
    UploadAudioWidget,
  },
  props: {
    id: {
      type: [Number, String],
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
  },
  data() {
    const defaultTemplates = {
      cameraAlert: `📷 Camera Alert\nCamera: \${cameraName} (ID: \${cameraId})\nDevice: \${deviceId}\nType: \${triggerType}\nTime: \${createTime}\n\${eventContent} on \${assetName} (\${deviceKey}) at \${timestamp:Asia/Hong_Kong} \n\${feedbackUrl}`,
      lockAlarm: `🔒 Lock Alarm\nLock: \${lockName} (ID: \${deviceId})\nAsset: \${assetId}\nAlarm Type: \${alarmType}\nTime: \${dateTime}\nDesc: \${describe}\nLocation: \${longitude}, \${latitude}\n\${eventContent} on \${assetName} (\${deviceKey}) at \${timestamp:Asia/Hong_Kong} \n\${feedbackUrl}`,
      watchAlarm: `⌚ Watch Alarm\nWatch: \${watchName} (ID: \${deviceId})\nIMEI: \${imei}\nType: \${type}\nTime: \${dateTime}\nAlert: \${alertInfo}\nMsg: \${hmsg}\nFence: \${fenceName} (ID: \${fenceId})\n\${eventContent} on \${assetName} (\${deviceKey}) at \${timestamp:Asia/Hong_Kong} \n\${feedbackUrl}`,
    };
    return {
      loading: false,
      submitting: false,
      defaultTemplates,
      form: new NotificationRuleModel({
        priority: 50,
        type: "event",
        template: defaultTemplates["cameraAlert"],
      }),
      typeVariables: {
        cameraAlert: [
          "${deviceId}",
          "${cameraId}",
          "${triggerType}",
          "${createTime}",
          "${cameraName}",
        ],
        lockAlarm: [
          "${deviceId}",
          "${assetId}",
          "${alarmType}",
          "${dateTime}",
          "${describe}",
          "${longitude}",
          "${latitude}",
          "${lockName}",
        ],
        watchAlarm: [
          "${deviceId}",
          "${imei}",
          "${type}",
          "${dateTime}",
          "${alertInfo}",
          "${hmsg}",
          "${fenceId}",
          "${fenceName}",
          "${watchName}",
        ],
      },
      commonVariables: [
        "${eventContent}",
        "${eventLevel}",
        "${deviceKey}",
        "${assetName}",
        "${ruleName}",
        "${eventName}",
        "${timestamp}",
        "${timestamp:Asia/Hong_Kong}",
        "${feedbackUrl}",
      ],
    };
  },
  computed: {
    cooldownScope() {
      return this.form.cooldown_by_param === "deviceKey" ? "device" : "rule";
    },
    availableVariables() {
      const type = this.form.type;
      if (type && this.typeVariables[type]) {
        return [...this.commonVariables, ...this.typeVariables[type]];
      }
      return this.commonVariables;
    },
    messagePreview() {
      let template = this.form.template;
      template = template.replace(/\${eventContent}/g, "Temperature too high");
      template = template.replace(/\${eventLevel}/g, "High");
      template = template.replace(/\${deviceKey}/g, "DVC-001");
      template = template.replace(/\${assetName}/g, "Server Room Sensor");
      template = template.replace(/\${ruleName}/g, "Temperature Alert Rule");
      template = template.replace(/\${eventName}/g, "Temperature Alert");
      template = template.replace(
        /\${timestamp:Asia\/Hong_Kong}/g,
        "2025-12-24 10:30:00"
      );
      template = template.replace(
        /\${feedbackUrl}/g,
        "http://example.com/feedback"
      );
      template = template.replace(/\${timestamp}/g, "2025-12-24 10:30:00");
      const type = this.form.type;
      if (type === "cameraAlert") {
        template = template.replace(/\${deviceId}/g, "CAM-001");
        template = template.replace(/\${cameraId}/g, "C123");
        template = template.replace(/\${triggerType}/g, "Motion");
        template = template.replace(/\${createTime}/g, "2025-12-24 10:30:00");
        template = template.replace(/\${cameraName}/g, "Main Entrance Camera");
      } else if (type === "lockAlarm") {
        template = template.replace(/\${deviceId}/g, "LOCK-001");
        template = template.replace(/\${assetId}/g, "A456");
        template = template.replace(/\${alarmType}/g, "Forced Open");
        template = template.replace(/\${dateTime}/g, "2025-12-24 10:30:00");
        template = template.replace(/\${describe}/g, "Door forced open");
        template = template.replace(/\${longitude}/g, "114.1694");
        template = template.replace(/\${latitude}/g, "22.3193");
        template = template.replace(/\${lockName}/g, "Server Room Lock");
      } else if (type === "watchAlarm") {
        template = template.replace(/\${deviceId}/g, "WATCH-001");
        template = template.replace(/\${imei}/g, "123456789012345");
        template = template.replace(/\${type}/g, "SOS");
        template = template.replace(/\${dateTime}/g, "2025-12-24 10:30:00");
        template = template.replace(/\${alertInfo}/g, "SOS Button Pressed");
        template = template.replace(/\${hmsg}/g, "Help needed!");
        template = template.replace(/\${fenceId}/g, "F789");
        template = template.replace(/\${fenceName}/g, "School Area");
        template = template.replace(/\${watchName}/g, "Student Watch");
      }
      return template;
    },
  },
  watch: {
    id: {
      handler() {
        this.fetchFormData();
      },
    },
    "form.type": {
      handler(newType, oldType) {
        if (
          newType &&
          this.defaultTemplates[newType] &&
          this.form.template === this.defaultTemplates[oldType]
        ) {
          this.form.template = this.defaultTemplates[newType];
        }
        if (
          !oldType &&
          newType &&
          this.defaultTemplates[newType] &&
          this.form.template === this.defaultTemplates["cameraAlert"]
        ) {
          this.form.template = this.defaultTemplates[newType];
        }
      },
    },
  },
  methods: {
    listNotificationRecipientInChannels,
    getNotificationRuleV2,
    NotificationRuleModel,
    updateNotificationRuleV2,
    createNotificationRuleV2,
    handleCooldownScopeChange(scope) {
      this.form.cooldown_by_param = scope === "device" ? "deviceKey" : "";
    },
    async handleSubmit() {
      try {
        this.submitting = true;
        const payload = new NotificationRuleModel(this.form);
        if (this.id) {
          await updateNotificationRuleV2(payload);
        } else {
          await createNotificationRuleV2(payload);
        }
        this.$message.success(
          `Notification rule ${this.id ? "updated" : "created"} successfully!`
        );
        this.$emit("confirm", this.form);
      } catch (error) {
        this.$message.error(
          extractErrorMessage(error, "Failed to save notification rule.")
        );
      } finally {
        this.submitting = false;
      }
    },
    async fetchFormData() {
      if (this.id) {
        try {
          this.loading = true;
          const rule = await getNotificationRuleV2(String(this.id));
          this.form = new NotificationRuleModel(rule);
          this.form.enabled =
            rule.enabled === 1 || rule.enabled === true || rule.enabled === "1";
        } finally {
          this.loading = false;
        }
      } else {
        this.form = new NotificationRuleModel({
          template: this.defaultTemplates["cameraAlert"],
        });
      }
    },
  },
  mounted() {
    if (this.id) {
      this.fetchFormData();
    }
  },
};
</script>
